import { useCallback, useEffect, useRef, useState } from 'react';
import { RetryAction, Button, Callout, EndLine, ListRow, Loading, Section, Waiting, useRowKeys } from '@beyond-js/ui/react';
import type { Capture, Files, FileText, Package } from '@snapshots/domain';
import { Copies } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import { Digest, Failure, Outage, Problems, Unanswered, useOutage } from '@snapshots/ui';
import type { Refusal } from '@snapshots/transport';

/**
 * What a snapshot holds (SNP-02, SNP-14), as the family's rows (D162, L1–L2): one row per package, its name,
 * then one quiet line with its version, its path and its size, and its Download as the row's one action. While
 * a package has not arrived its row leads with that state's mark and says so. Below each row, the context the
 * row cannot hold: its description and dependencies from the manifest it declared, its digest (copyable, with
 * what it means behind help), a damaged archive's cause, and its files, listed from the archive on request,
 * each opened in a viewer that shows text as text and never renders it. Rows stack at every width: nothing is
 * cut on a phone. A listing or a file nobody answered says "Not available right now" in its place, and the
 * page's one line says who did not answer and reads it again (D163).
 */
export function Packages({ capture }: { capture: Capture }) {
	const { text, count } = usePresence();
	// A package's arrival is said only while some have not arrived (L3)
	const arriving = capture.packages.some(entry => !entry.stored);
	const keys = useRowKeys();
	return (
		<Section title={text('capture.packages')} description={count('capture.packages.count', capture.packages.length)}>
			<ul className="bui-rows" data-framed data-packages ref={keys}>
				{capture.packages.map(entry => (
					<li key={entry.path} className="package-entry" data-package={entry.path} data-led={arriving ? '' : undefined}>
						<Entry capture={capture} entry={entry} arriving={arriving} />
					</li>
				))}
			</ul>
		</Section>
	);
}

/** One package: its row, and the context below it */
function Entry({ capture, entry, arriving }: { capture: Capture; entry: Package; arriving: boolean }) {
	const { text, size, captures, language } = usePresence();
	const description = typeof entry.manifest.description === 'string' ? entry.manifest.description : undefined;
	const dependencies = Object.entries(entry.dependencies ?? {});
	const readable = capture.state === 'active' && entry.stored;
	const archive = captures.archive(capture, entry.path);
	const received = entry.stored ? text('capture.package.received') : text('capture.package.unstored');
	return (
		<>
			<ListRow
				tag="div"
				title={entry.name}
				lead={arriving ? { state: entry.stored ? 'success' : 'running', label: received } : null}
				meta={[entry.version, <span key="path" className="font-mono">{entry.path}</span>, entry.stored ? size(entry.bytes) : received]}
				actions={readable ? [{ label: text('capture.download'), name: text('capture.download.named', { name: entry.name }), onSelect: () => window.location.assign(archive) }] : []}
			/>
			<div className="package-context bui-row-under flex flex-col gap-2">
				{description && <p className="text-small">{description}</p>}
				<dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-small">
					<dt className="text-ink-muted">{text('capture.package.digest')}</dt>
					<dd className="min-w-0">{entry.stored ? <Digest value={entry.digest} topic={text('capture.package.digest')} explanation={text('capture.digest.help')} labels={Copies.help(language)} /> : <span className="text-ink-muted">{text('capture.package.unstored')}</span>}</dd>
					<dt className="text-ink-muted">{text('capture.package.dependencies')}</dt>
					<dd className="min-w-0 break-all font-mono">{dependencies.length ? dependencies.map(([name, range]) => `${name} ${range}`).join(', ') : <span className="font-sans text-ink-muted">{text('capture.package.dependencies.none')}</span>}</dd>
				</dl>
				{entry.damaged && <Callout tone="danger" title={text('capture.package.damaged')} />}
				{readable && <FileList capture={capture} entry={entry} />}
			</div>
		</>
	);
}

/** A package's files, read from its archive when the person opens them */
function FileList({ capture, entry }: { capture: Capture; entry: Package }) {
	const { text, count, size, captures, language } = usePresence();
	const [files, setFiles] = useState<Files>();
	// A refusal is the listing's own answer; a listing nobody answered is the page's line
	const [problem, setProblem] = useState<Refusal>();
	const [missed, setMissed] = useState<Refusal>();
	const [open, setOpen] = useState<string>();
	const keys = useRowKeys();
	// One read of the listing, resolving whether it was answered
	const load = async (): Promise<boolean> => {
		try {
			setFiles(await captures.files(capture.id, entry.path));
			setProblem(undefined);
			setMissed(undefined);
			return true;
		} catch (cause) {
			const refusal = Problems.refusal(cause);
			if (refusal.quiet) return true;
			if (Outage.cause(refusal)) {
				setMissed(refusal);
				return false;
			}
			// A refusal is an answer: it ends the line
			setMissed(undefined);
			setProblem(refusal);
			return true;
		}
	};
	useOutage(`files:${entry.path}`, missed ? { title: text('capture.files.show', { name: entry.name }), refusal: missed, run: load } : null);
	const read = () => {
		setProblem(undefined);
		void load();
	};
	const listed = files?.files.filter(file => file.kind === 'file').length ?? 0;
	// The list's end: how many files it holds, or the cap when only the first entries are listed (L5)
	const end = files?.truncated ? text('capture.files.truncated', { count: files.files.length }) : count('capture.files.count', listed);
	return (
		<details className="text-small" data-files={entry.path} onToggle={event => (event.currentTarget as HTMLDetailsElement).open && !files && !missed && read()}>
			<summary className="cursor-pointer">{text('capture.files.show', { name: entry.name })}</summary>
			<div className="mt-2 flex flex-col gap-2">
				{missed && <Unanswered language={language} frame={false} />}
				{problem && <Failure refusal={problem} text={text} locale={language} attempt="read" actions={<RetryAction labels={{ retry: text('action.retry') }} onClick={read} />} />}
				{!files && !problem && !missed && <Waiting label={Loading.text({ name: text('loading.files'), language })} language={language} kind="lines" rows={3} frame={false} />}
				{files && !files.readable && <p>{text('capture.files.unreadable')}</p>}
				{files?.readable && !files.files.length && <p className="text-ink-muted">{text('capture.files.empty')}</p>}
				{files?.readable && files.files.length > 0 && (
					<>
						<ul className="bui-rows" ref={keys} aria-label={text('capture.files.show', { name: entry.name })}>
							{files.files.map(file => (
								<li key={file.name} data-file={file.name}>
									<ListRow
										tag="div"
										title={file.name}
										run={file.kind === 'file' ? () => setOpen(current => (current === file.name ? undefined : file.name)) : null}
										meta={file.kind === 'file' ? size(file.size) : text('capture.files.folder')}
									/>
									{open === file.name && <Viewer capture={capture} entry={entry} name={file.name} onClose={() => setOpen(undefined)} />}
								</li>
							))}
						</ul>
						<EndLine text={end} />
					</>
				)}
			</div>
		</details>
	);
}

/** One file as text, in a region of its own; a binary file says so and offers the package's download */
function Viewer({ capture, entry, name, onClose }: { capture: Capture; entry: Package; name: string; onClose: () => void }) {
	const { text, captures, language } = usePresence();
	const [file, setFile] = useState<FileText>();
	const [problem, setProblem] = useState<Refusal>();
	const [missed, setMissed] = useState<Refusal>();
	const live = useRef(true);
	// One read of the file, resolving whether it was answered
	const load = useCallback(async (): Promise<boolean> => {
		try {
			const found = await captures.file(capture.id, entry.path, name);
			if (live.current) {
				setFile(found);
				setMissed(undefined);
			}
			return true;
		} catch (cause) {
			const refusal = Problems.refusal(cause);
			if (!live.current || refusal.quiet) return true;
			if (Outage.cause(refusal)) {
				setMissed(refusal);
				return false;
			}
			setMissed(undefined);
			setProblem(refusal);
			return true;
		}
	}, [captures, capture.id, entry.path, name]);
	useEffect(() => {
		live.current = true;
		void load();
		return () => {
			live.current = false;
		};
	}, [load]);
	useOutage(`file:${entry.path}:${name}`, missed ? { title: text('outage.read.file', { name }), refusal: missed, run: load } : null);
	return (
		<section className="file-viewer w-full basis-full rounded-beyond border border-line bg-sunken" aria-label={name} data-viewer={name}>
			<div className="file-viewer-head">
				<span className="break-all font-mono">{name}</span>
				<Button small variant="quiet" label={text('capture.file.close')} onClick={onClose} />
			</div>
			{missed && <Unanswered language={language} frame={false} />}
			{problem && <Failure refusal={problem} text={text} locale={language} attempt="read" />}
			{!file && !problem && !missed && <Waiting label={Loading.text({ name: text('loading.file'), language })} language={language} kind="lines" rows={3} frame={false} />}
			{file?.binary && <p>{text('capture.file.binary')}</p>}
			{file?.truncated && !file.binary && <p className="text-ink-muted">{text('capture.file.truncated')}</p>}
			{/* The text scrolls in a holder of its own, so the text itself can trim its last line (D184). The holder is a
			    named region Tab reaches, so the keys scroll it in every engine: WebKit, unlike Chrome and Firefox,
			    never makes a scroller focusable by itself */}
			{file && !file.binary && (
				<div className="file-viewer-text max-h-[32rem] overflow-auto" tabIndex={0} role="region" aria-label={text('capture.file.text', { name })}>
					<pre className="whitespace-pre-wrap break-words font-mono text-small">{file.text}</pre>
				</div>
			)}
		</section>
	);
}
